import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plan, PlanAudience, PlanPeriod } from '../../../types/HomeTypes';
import { PLANS_PESSOAL, PLANS_TRABALHO } from '../../../utils/homeData';
import { fetchPublicPlans } from '../../../api/checkout';
import './PlansSection.scss';

type CarType = 'M' | 'G';

interface Props {
  sectionRef: React.RefObject<HTMLElement>;
}

function PlanCard({
  plan,
  period,
  carType,
  audience,
}: {
  plan: Plan;
  period: PlanPeriod;
  carType: CarType;
  audience: PlanAudience;
}) {
  const navigate = useNavigate();
  const mod = plan.highlight ? 'highlight' : 'default';
  const gPricing = carType === 'G' ? plan.pricingG : undefined;

  const goToCheckout = () => {
    const params = new URLSearchParams({ plan: plan.name, size: carType, period });
    if (audience === 'trabalho') params.set('professional', '1');
    navigate(`/checkout?${params.toString()}`);
  };

  const displayPrice = useCallback((): { integer: string; cents: string } => {
    if (period === 'anual') {
      const annualPrice = gPricing?.annualPrice ?? plan.annualPrice;
      const annualCents = gPricing?.annualCents ?? plan.annualCents;
      if (annualPrice !== 'sob') return { integer: annualPrice, cents: annualCents };
    }
    return { integer: gPricing?.price ?? plan.price, cents: gPricing?.cents ?? plan.cents };
  }, [plan, period, gPricing]);

  const economize = gPricing?.economize ?? plan.economize;
  const installment = gPricing?.installment ?? plan.installment;

  const { integer, cents } = displayPrice();

  return (
    <div className={`plans-section__card plans-section__card--${mod}`}>
      <div className="plans-section__card-top">
        {plan.badge === 'popular' && (
          <span className="plans-section__popular-badge">★ Mais popular</span>
        )}
        {plan.badge === 'premium' && (
          <span className="plans-section__premium-badge">PREMIUM</span>
        )}
        {plan.badge === 'none' && <div />}
        {plan.badge === 'completo' && (
          <span className="plans-section__popular-badge">★ Mais completo</span>
        )}
      </div>

      <div className={`plans-section__plan-name plans-section__plan-name--${mod}`}>{plan.name}</div>
      <div className={`plans-section__plan-subtitle plans-section__plan-subtitle--${mod}`}>{plan.subtitle}</div>

      <div className={`plans-section__price-from plans-section__price-from--${mod}`}>a partir de</div>
      <div className="plans-section__price-row">
        <span className={`plans-section__price-currency plans-section__price-currency--${mod}`}>R$</span>
        <span className={`plans-section__price-main plans-section__price-main--${mod}`}>{integer}</span>
        <span className={`plans-section__price-cents plans-section__price-cents--${mod}`}>,{cents}</span>
        <span className={`plans-section__price-period plans-section__price-period--${mod}`}>/mês*</span>
      </div>

      {period === 'anual' && (
        <>
          <div className={`plans-section__economize-badge plans-section__economize-badge--${mod}`}>
            ✓ {economize}
          </div>
          <br />
          <div className={`plans-section__installment-badge plans-section__installment-badge--${mod}`}>
            ≡ {installment}
          </div>
        </>
      )}

      {period === 'mensal' && plan.monthlyLabel && (
        <div className={`plans-section__monthly-label plans-section__monthly-label--${mod}`}>
          {plan.monthlyLabel}
        </div>
      )}

      {plan.notIncluded && (
        <div className={`plans-section__not-included plans-section__not-included--${mod}`}>
          ✗ {plan.notIncluded}
        </div>
      )}

      <div className={`plans-section__features plans-section__features--${mod}`}>
        {plan.features.map((feature) => (
          <div key={feature} className="plans-section__feature">
            <span className={`plans-section__feature-check plans-section__feature-check--${mod}`}>✓</span>
            <span className={`plans-section__feature-text plans-section__feature-text--${mod}`}>{feature}</span>
          </div>
        ))}
      </div>

      <button
        className={`plans-section__cta-btn plans-section__cta-btn--${mod}`}
        onClick={goToCheckout}
      >
        {plan.cta} →
      </button>
    </div>
  );
}

export function PlansSection({ sectionRef }: Props) {
  const [audience, setAudience] = useState<PlanAudience>('pessoal');
  const [period, setPeriod] = useState<PlanPeriod>('mensal');
  const [carType, setCarType] = useState<CarType>('M');

  // aquece o cache de planos p/ o checkout abrir já com o plano selecionado
  useEffect(() => {
    fetchPublicPlans().catch(() => undefined);
  }, []);

  const visiblePlans = audience === 'pessoal' ? PLANS_PESSOAL : PLANS_TRABALHO;
  const isAnnual = period === 'anual';

  return (
    <section id="plans-section" className="plans-section" ref={sectionRef as unknown as React.RefObject<HTMLDivElement>}>
      <div className="plans-section__header">
        <h2 className="section-title">Escolha o seu e assine agora mesmo</h2>
        <p className="plans-section__subtitle">
          Lavagens ilimitadas em todos os planos{' '}
          <strong style={{ color: '#2F3857' }}>a partir de R$ 69,90/mês</strong>
        </p>
      </div>

      <div className="plans-section__controls">
        <div className="plans-section__unlimited-badge">
          <span className="plans-section__unlimited-dot" />
          Lavagem ilimitada em todos os planos
        </div>
      </div>

      <div className="plans-section__audience-row">
        <div className="plans-section__audience-toggle">
          <button
            className={`plans-section__audience-btn plans-section__audience-btn--${audience === 'pessoal' ? 'active' : 'inactive'}`}
            onClick={() => setAudience('pessoal')}
          >
            Pra você
          </button>
          <button
            className={`plans-section__audience-btn plans-section__audience-btn--${audience === 'trabalho' ? 'active' : 'inactive'}`}
            onClick={() => setAudience('trabalho')}
          >
            Pra trabalhar
          </button>
        </div>
      </div>

      {audience === 'pessoal' && (
        <div className="plans-section__car-row">
          <div className="plans-section__car-toggle">
            <button
              className={`plans-section__car-btn plans-section__car-btn--${carType === 'M' ? 'active' : 'inactive'}`}
              onClick={() => setCarType('M')}
            >
              <span className="plans-section__car-btn-main">Carro M</span>
              <span className="plans-section__car-btn-sub">hatch/sedan</span>
            </button>
            <button
              className={`plans-section__car-btn plans-section__car-btn--${carType === 'G' ? 'active' : 'inactive'}`}
              onClick={() => setCarType('G')}
            >
              <span className="plans-section__car-btn-main">Carro G</span>
              <span className="plans-section__car-btn-sub">SUV/caminhonete</span>
            </button>
          </div>
        </div>
      )}

      <div className="plans-section__period-row">
        <span className="plans-section__period-label">Mensal</span>
        <div
          className={`plans-section__toggle-track plans-section__toggle-track--${isAnnual ? 'on' : 'off'}`}
          onClick={() => setPeriod(isAnnual ? 'mensal' : 'anual')}
        >
          <div className={`plans-section__toggle-knob plans-section__toggle-knob--${isAnnual ? 'on' : 'off'}`} />
        </div>
        <span className="plans-section__period-label">Anual</span>
        {isAnnual && <span className="plans-section__annual-badge">até 33% off</span>}
      </div>

      <div className={`plans-section__cards${audience === 'trabalho' ? ' plans-section__cards--two' : ''}`}>
        {visiblePlans.map((plan) => (
          <PlanCard key={plan.id} plan={plan} period={period} carType={carType} audience={audience} />
        ))}
      </div>

      <p className="plans-section__suv-note">
        *Carros G (SUVs): acréscimo de 15% sobre os valores exibidos.
      </p>
      <div className="plans-section__info-box">
        <span style={{ fontSize: '20px' }}>🚗</span>
        <span>
          Lavagens ilimitadas distribuídas na rede. Limitado a até 5 lavagens no mesmo parceiro, no mesmo mês — regra de uso consciente.
        </span>
      </div>
    </section>
  );
}
